import { useEffect } from 'react';
import { VideoElement } from '../../types/player';

export interface PlayerEventHandlers {
  onPlay?: () => void;
  onPause?: () => void;
  onTimeUpdate?: () => void;
  onDurationChange?: () => void;
  onProgress?: () => void;
  onWaiting?: () => void;
  onPlaying?: () => void;
  onEnded?: () => void;
  onError?: (e: any) => void;
  onLoadedMetadata?: () => void;
  onLoadedData?: () => void;
  onCanPlay?: () => void;
}

export const usePlayerEvents = (
  videoElement: VideoElement,
  playerRef: React.RefObject<HTMLMediaElement>,
  handlers: PlayerEventHandlers,
) => {
  useEffect(() => {
    if (!videoElement && !playerRef.current) return;

    const elements = [playerRef.current, videoElement].filter(Boolean);

    // Function to add event listeners to an element
    const addEvents = (element: HTMLMediaElement) => {
      // Core playback events
      if (handlers.onPlay) element.addEventListener('play', handlers.onPlay);
      if (handlers.onPause) element.addEventListener('pause', handlers.onPause);
      if (handlers.onTimeUpdate) element.addEventListener('timeupdate', handlers.onTimeUpdate);
      if (handlers.onEnded) element.addEventListener('ended', handlers.onEnded);

      // Loading events
      if (handlers.onLoadedMetadata)
        element.addEventListener('loadedmetadata', handlers.onLoadedMetadata);
      if (handlers.onLoadedData) element.addEventListener('loadeddata', handlers.onLoadedData);
      if (handlers.onCanPlay) element.addEventListener('canplay', handlers.onCanPlay);
      if (handlers.onDurationChange)
        element.addEventListener('durationchange', handlers.onDurationChange);

      // Buffering events
      if (handlers.onWaiting) element.addEventListener('waiting', handlers.onWaiting);
      if (handlers.onPlaying) element.addEventListener('playing', handlers.onPlaying);

      // Error handling
      if (handlers.onError) element.addEventListener('error', handlers.onError);
    };

    // Function to remove event listeners from an element
    const removeEvents = (element: HTMLMediaElement) => {
      if (handlers.onPlay) element.removeEventListener('play', handlers.onPlay);
      if (handlers.onPause) element.removeEventListener('pause', handlers.onPause);
      if (handlers.onTimeUpdate) element.removeEventListener('timeupdate', handlers.onTimeUpdate);
      if (handlers.onEnded) element.removeEventListener('ended', handlers.onEnded);
      if (handlers.onLoadedMetadata)
        element.removeEventListener('loadedmetadata', handlers.onLoadedMetadata);
      if (handlers.onLoadedData) element.removeEventListener('loadeddata', handlers.onLoadedData);
      if (handlers.onCanPlay) element.removeEventListener('canplay', handlers.onCanPlay);
      if (handlers.onDurationChange)
        element.removeEventListener('durationchange', handlers.onDurationChange);
      if (handlers.onWaiting) element.removeEventListener('waiting', handlers.onWaiting);
      if (handlers.onPlaying) element.removeEventListener('playing', handlers.onPlaying);
      if (handlers.onError) element.removeEventListener('error', handlers.onError);
    };

    // Add events to all elements (player and video)
    elements.forEach((element) => {
      if (element) addEvents(element as HTMLMediaElement);
    });

    // Remove events when component unmounts
    return () => {
      elements.forEach((element) => {
        if (element) removeEvents(element as HTMLMediaElement);
      });
    };
  }, [videoElement, playerRef.current, handlers]);
};
